import React, { useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';

import Modal from 'common/components/Modal';
import Icon from 'common/components/Icon';
import Typography from 'common/components/Typography';
import colors from 'common/styles/colors';
import { api } from 'app/services';

import {
  euroToXmnAmount,
  truncateToXmnDecimals,
} from './steps/AmountInput/utils';
import {
  WalletInputStep,
  ScreeningFailedStep,
  AmountInputStep,
  SuccessStep,
} from './steps';
import { STEPS, ACTIONS, INITIAL_STATE } from './constants';
import styles from './index.module.css';

const WALLET_SCREENING_STATUS = {
  APPROVED: 'approved',
  REJECTED: 'rejected',
  SKIPPED: 'skipped',
};

const extractScreeningStatus = (response) => {
  const screeningCollection =
    response?.walletScreening ?? response?.withdrawals ?? null;

  const normalizedEntity = screeningCollection
    ? Object.values(screeningCollection)[0]
    : null;

  const status = normalizedEntity?.attributes?.status;

  return typeof status === 'string' ? status.toLowerCase() : '';
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_WALLET_ADDRESS:
      return { ...state, walletAddress: action.payload };
    case ACTIONS.SET_NETWORK:
      return { ...state, network: action.payload };
    case ACTIONS.SET_CONFIRMED:
      return { ...state, isConfirmed: action.payload };
    case ACTIONS.SET_AMOUNT:
      return { ...state, amount: action.payload, isMaxAmount: false };
    case ACTIONS.SET_IS_MAX_AMOUNT:
      return { ...state, isMaxAmount: action.payload };
    case ACTIONS.SET_WALLET_SCREENING_SUBMITTING:
      return {
        ...state,
        isWalletScreeningSubmitting: action.payload,
        walletScreeningError: null,
      };
    case ACTIONS.SET_WALLET_SCREENING_ERROR:
      return {
        ...state,
        walletScreeningError: action.payload,
        isWalletScreeningSubmitting: false,
      };
    case ACTIONS.SET_SUBMITTING:
      return { ...state, isSubmitting: action.payload, error: null };
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isSubmitting: false };
    case ACTIONS.GO_TO_STEP:
      return { ...state, step: action.payload };
    case ACTIONS.RESET:
      return INITIAL_STATE;
    default:
      return state;
  }
};

const WithdrawModal = ({
  isOpen,
  onClose,
  onWithdrawSuccess,
  xmnRate,
  xmnBalance,
  xmnBalanceRaw,
  euroBalance,
}) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const handleClose = useCallback(() => {
    const wasSuccessful = state.step === STEPS.SUCCESS;
    dispatch({ type: ACTIONS.RESET });
    onClose();
    if (wasSuccessful) onWithdrawSuccess?.();
  }, [onClose, onWithdrawSuccess, state.step]);

  const goToStep = useCallback((step) => {
    dispatch({ type: ACTIONS.GO_TO_STEP, payload: step });
    if (step !== STEPS.AMOUNT_INPUT) {
      dispatch({ type: ACTIONS.SET_AMOUNT, payload: '' });
    }
    dispatch({ type: ACTIONS.SET_ERROR, payload: null });
  }, []);

  const handleWalletAddressChange = useCallback((evt) => {
    dispatch({ type: ACTIONS.SET_WALLET_ADDRESS, payload: evt.target.value });
  }, []);

  const handleNetworkChange = useCallback((value) => {
    dispatch({ type: ACTIONS.SET_NETWORK, payload: value });
  }, []);

  const handleConfirmationChange = useCallback((evt) => {
    dispatch({ type: ACTIONS.SET_CONFIRMED, payload: evt.target.checked });
  }, []);

  const handleWalletSubmit = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_WALLET_SCREENING_SUBMITTING, payload: true });

    const { response, error } = await api.screenWithdrawalWallet({
      address: state.walletAddress,
      chain: state.network,
    });

    if (error) {
      dispatch({ type: ACTIONS.SET_WALLET_SCREENING_ERROR, payload: error });
      return;
    }

    const screeningStatus = extractScreeningStatus(response);

    dispatch({
      type: ACTIONS.SET_WALLET_SCREENING_SUBMITTING,
      payload: false,
    });

    if (
      screeningStatus === WALLET_SCREENING_STATUS.APPROVED ||
      screeningStatus === WALLET_SCREENING_STATUS.SKIPPED
    ) {
      goToStep(STEPS.AMOUNT_INPUT);
      return;
    }

    if (screeningStatus === WALLET_SCREENING_STATUS.REJECTED) {
      goToStep(STEPS.SCREENING_FAILED);
      return;
    }

    dispatch({
      type: ACTIONS.SET_WALLET_SCREENING_ERROR,
      payload: 'Unexpected wallet screening status',
    });
  }, [state.walletAddress, state.network, goToStep]);

  const handleRetryWallet = useCallback(() => {
    dispatch({ type: ACTIONS.SET_WALLET_ADDRESS, payload: '' });
    dispatch({ type: ACTIONS.SET_NETWORK, payload: '' });
    dispatch({ type: ACTIONS.SET_CONFIRMED, payload: false });
    dispatch({ type: ACTIONS.SET_WALLET_SCREENING_ERROR, payload: null });
    goToStep(STEPS.WALLET_INPUT);
  }, [goToStep]);

  const handleAmountChange = useCallback((value, isMax = false) => {
    dispatch({ type: ACTIONS.SET_AMOUNT, payload: value });
    if (isMax) {
      dispatch({ type: ACTIONS.SET_IS_MAX_AMOUNT, payload: true });
    }
  }, []);

  const handleAmountSubmit = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_SUBMITTING, payload: true });

    const xmnAmount = state.isMaxAmount
      ? truncateToXmnDecimals(xmnBalanceRaw)
      : euroToXmnAmount(state.amount, xmnRate);

    const { response, error } = await api.createWithdrawal({
      walletAddress: state.walletAddress,
      xmnAmount,
      blockchain: state.network,
    });

    if (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error });
      return;
    }

    if (response) {
      goToStep(STEPS.SUCCESS);
    }
  }, [
    state.amount,
    state.isMaxAmount,
    state.walletAddress,
    state.network,
    xmnRate,
    xmnBalanceRaw,
    goToStep,
  ]);

  const renderStep = () => {
    switch (state.step) {
      case STEPS.WALLET_INPUT:
        return (
          <WalletInputStep
            walletAddress={state.walletAddress}
            network={state.network}
            isConfirmed={state.isConfirmed}
            isSubmitting={state.isWalletScreeningSubmitting}
            error={state.walletScreeningError}
            onWalletAddressChange={handleWalletAddressChange}
            onNetworkChange={handleNetworkChange}
            onConfirmationChange={handleConfirmationChange}
            onSubmit={handleWalletSubmit}
          />
        );
      case STEPS.SCREENING_FAILED:
        return <ScreeningFailedStep onRetry={handleRetryWallet} />;
      case STEPS.AMOUNT_INPUT:
        return (
          <AmountInputStep
            euroAmount={state.amount}
            xmnRate={xmnRate}
            xmnBalance={xmnBalance}
            euroBalance={euroBalance}
            isSubmitting={state.isSubmitting}
            error={state.error}
            onAmountChange={handleAmountChange}
            onSubmit={handleAmountSubmit}
          />
        );
      case STEPS.SUCCESS:
        return <SuccessStep />;
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnClickOutside={false}
      closeOnEscape={false}
      modalClassName={styles.modal}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <Typography
              tag="h2"
              size="sm"
              weight="semiBold"
              color="color-neutral-900"
            >
              Withdraw rewards
            </Typography>
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
              aria-label="Close modal"
            >
              <Icon.CrossV2
                size="medium"
                fill={colors['rebranding-md-color-grey-50']}
                description="Close"
              />
            </button>
          </div>
        </div>
        {renderStep()}
      </div>
    </Modal>
  );
};

WithdrawModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onWithdrawSuccess: PropTypes.func,
  xmnRate: PropTypes.number,
  xmnBalance: PropTypes.number,
  xmnBalanceRaw: PropTypes.string,
  euroBalance: PropTypes.number,
};

WithdrawModal.defaultProps = {
  onWithdrawSuccess: undefined,
  xmnRate: 0,
  xmnBalance: 0,
  xmnBalanceRaw: '0',
  euroBalance: 0,
};

export default WithdrawModal;
